import React, { useState } from "react";
import { HelpCircle, X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { getTour } from "../lib/tours";

/**
 * Reusable presentational modal for a guided tour.
 * Consumed by GuidedTourButton (manual open via header button) AND
 * WelcomeTourAutoLaunch (auto-open on first login).
 *
 * Props:
 *   open        - controlled boolean
 *   onClose     - called when user hits X, backdrop, or Finish
 *   tour        - { title, steps: [{title, body}] }
 *   overline    - optional short label above the title (e.g. "Welcome!")
 */
export function TourModal({ open, onClose, tour, overline }) {
  const { lang } = useI18n();
  const [step, setStep] = useState(0);
  React.useEffect(() => { if (open) setStep(0); }, [open]);
  if (!open || !tour) return null;
  const total = tour.steps.length;
  const cur = tour.steps[step];

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="guided-tour-backdrop"
    >
      <div
        data-testid="guided-tour-modal"
        className="bg-white rounded-[20px] max-w-[480px] w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 bg-gradient-to-br from-[#1e293b] via-[#475569] to-[#334155] text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={16} />
              <div className="text-[10px] uppercase tracking-[0.14em] text-white/80">
                {overline || (lang === "pt" ? "Tour guiado" : "Guided tour")}
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="tour-close"><X size={18} /></button>
          </div>
          <h3 className="text-[18px] font-semibold mt-2">{tour.title}</h3>
        </div>
        <div className="p-5">
          <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#94a3b8] mb-2">{step + 1}/{total} · {cur.title}</div>
          <p className="text-[13.5px] text-[#1e293b] leading-relaxed whitespace-pre-line">{cur.body}</p>
          <div className="mt-5 h-1 rounded-full bg-[#f1f5f9] overflow-hidden">
            <div className="h-full bg-[#475569]" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
        </div>
        <div className="px-5 pb-5 flex items-center justify-between gap-2">
          <button
            data-testid="tour-prev"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="sw-btn-secondary !text-[12px] disabled:opacity-40"
          ><ChevronLeft size={13} /> {lang === "pt" ? "Anterior" : "Back"}</button>
          {step < total - 1 ? (
            <button
              data-testid="tour-next"
              onClick={() => setStep(step + 1)}
              className="sw-btn-primary !text-[12px]"
            >{lang === "pt" ? "Próximo" : "Next"} <ChevronRight size={13} /></button>
          ) : (
            <button
              data-testid="tour-finish"
              onClick={onClose}
              className="sw-btn-primary !text-[12px]"
            >{lang === "pt" ? "Concluir" : "Finish"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GuidedTourButton({ pageKey, className = "" }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  // Persona-aware tour lookup (Sprint 42): the Travel tour in EN ships
  // separate flows for employee / manager / hr — getTour picks the right one
  // based on the current user's role.
  const tour = getTour(pageKey, lang, user?.role);
  if (!tour) return null;

  return (
    <>
      <button
        data-testid="guided-tour-btn"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-[#cbd5e1] bg-white text-[#475569] hover:border-[#475569] hover:bg-[#f8fafc] transition-all ${className}`}
        title={lang === "pt" ? "Tour guiado" : "Guided tour"}
      >
        <HelpCircle size={13} />
        <span className="hidden md:inline">{lang === "pt" ? "Tour guiado" : "Guided tour"}</span>
      </button>
      <TourModal open={open} onClose={() => setOpen(false)} tour={tour} />
    </>
  );
}
